'use client';
import { useMemo } from 'react';
import { useController } from 'react-hook-form';
import { TextField } from '@/components/shared/text-field';
import { useTranslation } from '@/contexts/language-context';
import { emailValidation } from '@/lib/const';
import { maskPhone, isValidPhone, phoneGhost } from '@/lib/phone';

// Anything with a letter or @ is treated as an email; otherwise phone
const isEmailLike = (val) => /[a-z@]/i.test(val || '');

/**
 * Sign-in account input: one field for phone or email. Typing digits
 * formats as a phone number with the mask remainder ghosted inline;
 * typing letters switches the header (and validation) to email.
 */
export function AccountField({
  control,
  name = 'account',
  label,
  labelEnd,
  autoComplete = 'username',
  allowEmail = false,
  onValueChange,
  onBlurValue
}) {
  const { t } = useTranslation();

  const rules = useMemo(
    () => ({
      required: t(
        'login.account.account_required',
        'Please enter an email or phone number'
      ),
      validate: (val) => {
        if (isEmailLike(val)) {
          return (
            new RegExp(emailValidation).test(val.trim()) ||
            t(
              'login.error.email_invalid',
              'Please enter a valid email address.'
            )
          );
        }
        return (
          isValidPhone(val) ||
          t(
            'login.error.phone_invalid',
            'Please enter a valid phone number without the country code.'
          )
        );
      }
    }),
    [t]
  );

  const { field, fieldState } = useController({ name, control, rules });
  const currentValue = field.value || '';
  const isEmail = isEmailLike(currentValue);

  const handleChange = (e) => {
    const raw = e.target.value;
    const next = isEmailLike(raw) ? raw : maskPhone(raw);
    field.onChange(next);
    onValueChange?.(next);
  };

  const handleClear = () => {
    field.onChange('');
    onValueChange?.('');
  };

  const handleBlur = () => {
    field.onBlur();
    onBlurValue?.(currentValue);
  };

  const ghost = isEmail ? '' : phoneGhost(currentValue);

  return (
    <TextField
      label={label}
      labelEnd={labelEnd}
      placeholder={
        isEmail
          ? t('login.label.email', 'Email')
          : allowEmail
            ? t('login.label.accountOrEmail', 'Phone Number or Email')
            : t('login.label.account', 'Phone Number')
      }
      type="text"
      // A 'tel' pad gives email accounts no way to type a letter or @, so
      // sign-in uses the email keyboard (digits are one tap away).
      inputMode={allowEmail || isEmail ? 'email' : 'tel'}
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      autoComplete={autoComplete}
      name={field.name}
      ref={field.ref}
      value={currentValue}
      onChange={handleChange}
      onBlur={handleBlur}
      error={fieldState.error}
      ghost={ghost}
      clearable
      onClear={handleClear}
    />
  );
}
